import { useState } from "react";
import { KeyRound, ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";
import { useAuthStore } from "../store/useAuthStore";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";

// Shown to a freshly-provisioned Client on first login (mustChangePassword).
// Two steps: set a new password (the temporary one dies here), then confirm
// profile details. Completing it clears the gate and reveals the portal.
const ClientOnboardingGate = () => {
  const { authUser, changePassword, checkAuth } = useAuthStore();
  const [step, setStep] = useState<1 | 2>(1);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const [profile, setProfile] = useState({
    email: authUser?.email?.endsWith("@client.cre.local") ? "" : authUser?.email || "",
    phone: "",
    company: "",
  });

  const submitPassword = async () => {
    if (pw.length < 6) return toast.error("Password must be at least 6 characters.");
    if (pw !== confirm) return toast.error("Passwords do not match.");
    setBusy(true);
    const ok = await changePassword({ newPassword: pw });
    setBusy(false);
    if (ok) setStep(2);
  };

  const submitProfile = async () => {
    setBusy(true);
    try {
      await axiosInstance.post("/client-portal/verify-profile", profile);
      toast.success("Welcome aboard!");
      await checkAuth(); // refresh authUser → gate closes
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Could not save profile");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-gradient-to-br from-blue-50 via-sky-50 to-slate-50 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
        <div className="bg-[#0058c3] px-6 py-5 text-white">
          <h1 className="text-lg font-bold flex items-center gap-2">
            {step === 1 ? <KeyRound size={18} /> : <ShieldCheck size={18} />}
            {step === 1 ? "Set your password" : "Confirm your details"}
          </h1>
          <p className="text-[12px] text-blue-100 mt-0.5">
            Welcome{authUser?.fullName ? `, ${authUser.fullName.split(" ")[0]}` : ""} — a couple of quick steps to secure your account.
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 px-6 pt-4">
          {[1, 2].map((s) => (
            <div key={s} className={`h-1.5 flex-1 rounded-full ${step >= s ? "bg-[#0058c3]" : "bg-slate-200"}`} />
          ))}
        </div>

        {step === 1 ? (
          <div className="p-6 space-y-4">
            <p className="text-[13px] text-slate-500">
              For security, please replace your temporary password with one only you know.
            </p>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">New password</label>
              <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 6 characters"
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0058c3]/30" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Confirm password</label>
              <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password"
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0058c3]/30" />
            </div>
            <button onClick={submitPassword} disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#0058c3] text-white font-semibold py-2.5 rounded-lg hover:bg-[#004b9e] disabled:opacity-50">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Continue
            </button>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <p className="text-[13px] text-slate-500">Confirm your contact details so we can keep you updated.</p>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Email</label>
              <input type="email" value={profile.email} onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))} placeholder="you@example.com"
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0058c3]/30" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Phone</label>
                <input value={profile.phone} onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))} placeholder="Optional"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0058c3]/30" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Company</label>
                <input value={profile.company} onChange={(e) => setProfile((p) => ({ ...p, company: e.target.value }))} placeholder="Optional"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0058c3]/30" />
              </div>
            </div>
            <button onClick={submitProfile} disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#0058c3] text-white font-semibold py-2.5 rounded-lg hover:bg-[#004b9e] disabled:opacity-50">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Enter my portal
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientOnboardingGate;
